import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, CardContent, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeFilter from '../DateRangeFilter';
import { CATEGORY_CHOICES, CURRENCY_CHOICES } from '../../utils/expenseUtils';

const ExpenseFilters = ({
  searchTerm,
  categoryFilter,
  currencyFilter,
  moneyAccountFilter,
  dateRange,
  filterOptions,
  moneyAccounts,
  onSearchChange,
  onCategoryFilterChange,
  onCurrencyFilterChange,
  onMoneyAccountFilterChange,
  onDateRangeChange,
  onClearFilters,
}) => {
  // Use dynamic filter options or fallback to constants
  const categoryOptions =
    filterOptions?.categories?.map((cat) => ({ value: cat, label: cat })) ||
    CATEGORY_CHOICES.map((cat) => ({ value: cat, label: cat }));
  const currencyOptions =
    filterOptions?.currencies?.map((curr) => ({ value: curr, label: curr })) ||
    CURRENCY_CHOICES.map((curr) => ({ value: curr, label: curr }));
  const moneyAccountOptions =
    moneyAccounts?.map((account) => ({
      value: account.id.toString(),
      label: `${account.name} (${account.currency})`,
    })) || [];
  return (
    <Grid item xs={12}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Autocomplete
                size="small"
                value={{ value: categoryFilter, label: categoryFilter }}
                onChange={(event, newValue) => onCategoryFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...categoryOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Category" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Autocomplete
                size="small"
                value={{ value: currencyFilter, label: currencyFilter }}
                onChange={(event, newValue) => onCurrencyFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...currencyOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Currency" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.2}>
              <Autocomplete
                size="small"
                value={
                  moneyAccountFilter === 'All'
                    ? { value: 'All', label: 'All' }
                    : moneyAccountOptions.find((option) => option.value === moneyAccountFilter) || {
                        value: 'All',
                        label: 'All',
                      }
                }
                onChange={(event, newValue) => onMoneyAccountFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...moneyAccountOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Money Account" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={dateRange.preset === 'custom' ? 2.8 : 1.7}>
              <DateRangeFilter
                onDateRangeChange={onDateRangeChange}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                selectedPreset={dateRange.preset}
                sx={{ height: '40px' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.2}>
              <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px' }}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

ExpenseFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  categoryFilter: PropTypes.string.isRequired,
  currencyFilter: PropTypes.string.isRequired,
  moneyAccountFilter: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    currencies: PropTypes.arrayOf(PropTypes.string),
  }),
  moneyAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
    })
  ),
  onSearchChange: PropTypes.func.isRequired,
  onCategoryFilterChange: PropTypes.func.isRequired,
  onCurrencyFilterChange: PropTypes.func.isRequired,
  onMoneyAccountFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

ExpenseFilters.defaultProps = {
  filterOptions: {},
  moneyAccounts: [],
};

export default ExpenseFilters;
